import React from 'react';
import { Form, Field } from 'react-final-form';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import { closeModal, updateConversationInHistory } from '../../../../../store/chat/slice';
import { updateConversationTitle as updateTitle } from '../../../../../api/chatApi';
import { Modal, Button, TextInput } from '../../../../../components/UI';
import styles from './ConversationModals.module.scss';

interface FormValues {
  title: string;
}

const RenameConversationModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { modal, conversationHistory } = useAppSelector((state) => state.chat);

  const isOpen = modal?.type === 'rename';
  const conversationId = modal?.type === 'rename' ? modal.conversationId : undefined;

  const currentTitle = React.useMemo(() => {
    if (!conversationId) return '';
    const conversation = conversationHistory.find((c) => c.id === conversationId);
    return conversation?.title || '';
  }, [conversationId, conversationHistory]);

  const handleClose = () => {
    dispatch(closeModal());
  };

  const handleSubmit = async (values: FormValues) => {
    if (!conversationId) return;

    try {
      const { conversation: updatedConversation } = await updateTitle(conversationId, values.title);
      dispatch(
        updateConversationInHistory({
          id: conversationId,
          updates: { title: updatedConversation.title },
        }),
      );

      dispatch(closeModal());
    } catch (error) {
      console.error('Failed to rename conversation:', error);
    }
  };

  const validate = (values: FormValues) => {
    const errors: Partial<FormValues> = {};
    if (!values.title || values.title.trim() === '') {
      errors.title = 'Title cannot be empty';
    }
    return errors;
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Rename conversation">
      <Form
        onSubmit={handleSubmit}
        validate={validate}
        initialValues={{ title: currentTitle }}
        render={({ handleSubmit, submitting, pristine, valid }) => (
          <form onSubmit={handleSubmit}>
            <div className={styles.modalContent}>
              <Field name="title">
                {({ input, meta }) => (
                  <TextInput
                    {...input}
                    label="Title"
                    error={meta.touched && meta.error}
                    placeholder="Enter a title for this conversation"
                    autoFocus
                    fullWidth
                  />
                )}
              </Field>
            </div>
            <div className={styles.modalActions}>
              <Button variant="secondary" onClick={handleClose} disabled={submitting} type="button">
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={submitting || pristine || !valid}
                loading={submitting}
                type="submit"
              >
                Save
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default RenameConversationModal;
